/**
 * Standard query options for React Query hooks
 *
 * These options help maintain consistency across the application and optimize
 * cache behavior based on data characteristics.
 */

/**
 * STATIC_DATA - For rarely changing data
 * Examples: Shop info, services, subscription plans, reviews
 *
 * - Long staleTime since data rarely changes
 * - Longer cache time to reduce API calls
 * - Standard retry attempts for resilience
 */
export const STATIC_DATA_OPTIONS = {
  staleTime: 15 * 60 * 1000, // 15 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes
  retry: 3,
} as const;

/**
 * USER_DATA - For user-specific data with medium change frequency
 * Examples: User appointments, provider memberships, provider schedules
 *
 * - Medium staleTime to balance freshness and performance
 * - Standard cache time
 * - Moderate retry attempts
 */
export const USER_DATA_OPTIONS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 20 * 60 * 1000, // 20 minutes
  retry: 2,
} as const;

/**
 * LIVE_DATA - For frequently changing data
 * Examples: Daily metrics, analytics, revenue data
 *
 * - Short staleTime for fresher data
 * - Shorter cache time
 * - Minimal retry to fail fast
 */
export const LIVE_DATA_OPTIONS = {
  staleTime: 2 * 60 * 1000, // 2 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
  retry: 1,
} as const;

/**
 * REALTIME_DATA - For data updated via WebSocket connections
 * Examples: Queue position, queue details, live payment status
 *
 * - Longer staleTime since sockets handle updates
 * - Disable automatic refetching (sockets provide updates)
 * - Short cache time
 * - Minimal retry
 */
export const REALTIME_DATA_OPTIONS = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  retry: 1,
} as const;

/**
 * VALIDATION_DATA - For existence checks and validations
 * Examples: Shop lookup, provider check, invitation validation
 *
 * - No retry (404s are expected responses)
 * - Medium staleTime to cache validation results
 * - Standard cache time
 */
export const VALIDATION_DATA_OPTIONS = {
  retry: false,
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 15 * 60 * 1000, // 15 minutes
} as const;

/**
 * MUTABLE_DATA - For provider-editable data
 * Examples: Services, pricing, schedules that providers frequently update
 *
 * - Very short staleTime for immediate freshness after edits
 * - Standard cache time for offline support
 * - Standard retry attempts
 */
export const MUTABLE_DATA_OPTIONS = {
  staleTime: 30 * 1000, // 30 seconds
  gcTime: 10 * 60 * 1000, // 10 minutes
  retry: 2,
} as const;
